import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BrandMark } from '@rynora/theme';
import { ArrowLeft, BookOpen } from 'lucide-react';
import '../../legal/legal.css';
import '../docs.css';

interface DocsLayoutProps {
  /** Page heading. */
  title: string;
  /** Last reviewed / verified date. */
  updated: string;
  /** Optional one-line summary above the body. */
  summary?: string;
  children: ReactNode;
}

const DOCS_SECTIONS: { title: string; items: { to: string; label: string }[] }[] = [
  {
    title: 'Get started',
    items: [
      { to: '/docs', label: 'Overview' },
      { to: '/docs/getting-started', label: 'Quickstart' },
    ],
  },
  {
    title: 'Account & billing',
    items: [
      { to: '/docs/plans', label: 'Plans & limits' },
      { to: '/docs/billing', label: 'Billing & invoices' },
      { to: '/docs/user-roles', label: 'User roles' },
      { to: '/docs/delete-and-restore', label: 'Delete & restore' },
    ],
  },
  {
    title: 'Authoring',
    items: [{ to: '/docs/script-step', label: 'Custom Script step' }],
  },
  {
    title: 'Reference',
    items: [
      { to: '/docs/browser-tiers', label: 'Browser tiers' },
      { to: '/docs/api', label: 'API reference' },
      { to: '/docs/self-hosting', label: 'Self-hosting' },
    ],
  },
];

/**
 * Shared chrome for the /docs section. Uses the same visual language as
 * <LegalLayout> for consistency, but with a docs-specific sidebar.
 */
export function DocsLayout({ title, updated, summary, children }: DocsLayoutProps) {
  const { pathname } = useLocation();

  return (
    <div className="legal-shell">
      <header className="legal-header">
        <div className="container legal-header-inner">
          <Link to="/" className="nav-logo" aria-label="Rynora — home">
            <BrandMark variant="compact" />
          </Link>
          <div className="legal-header-actions">
            <Link to="/" className="nav-auth-btn">
              <ArrowLeft size={14} /> Home
            </Link>
          </div>
        </div>
      </header>

      <div className="container legal-body">
        <aside className="legal-sidebar" aria-label="Documentation">
          <div className="docs-sidebar-title">
            <BookOpen size={14} /> Documentation
          </div>
          {DOCS_SECTIONS.map((section) => (
            <div key={section.title} className="docs-sidebar-section">
              <h4>{section.title}</h4>
              <nav>
                <ul>
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <Link to={item.to} className={pathname === item.to ? 'is-active' : ''}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          ))}
          <p className="legal-sidebar-note">
            Found a doc bug? <a href="mailto:support@rynora.app">support@rynora.app</a>
          </p>
        </aside>

        <article className="legal-article">
          <header className="legal-article-head">
            <h1>{title}</h1>
            <p className="legal-meta">Last verified {updated}</p>
            {summary && <p className="legal-summary">{summary}</p>}
          </header>
          <div className="legal-content">{children}</div>
        </article>
      </div>

      <footer className="legal-footer">
        <div className="container legal-footer-inner">
          <span>&copy; 2026 Rynora.</span>
          <span className="footer-bottom-links">
            <Link to="/docs">Docs</Link>
            <Link to="/status">Status</Link>
            <Link to="/contact">Contact</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
